import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Trash2, Eye, Send, Heart, ChevronUp, RotateCcw, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { optimizeMediaUrl } from '../lib/media';

const STORY_DURATION_MS = 5000;  // 5s for image; video uses its own duration
const STALL_TIMEOUT_MS = 8000;   // if media doesn't load within 8s → retry state
const SPINNER_DELAY_MS = 250;    // defer spinner for cached images so it doesn't flash



function initials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

export default function StoryViewer() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stories, setStories] = useState([]);
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [touchStartY, setTouchStartY] = useState(null);
  const [dragY, setDragY] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [storyAnimKey, setStoryAnimKey] = useState(0);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState({ count: 0, viewers: [] });
  const [replyText, setReplyText] = useState('');
  const [replySent, setReplySent] = useState(false);
  const [replySending, setReplySending] = useState(false);
  // Q16 — media gate: overlays MUST NOT render until the media frame is on
  // screen, otherwise users see floating text on a black void while the image
  // is still in flight. Reset to false every time the story index changes.
  const [mediaLoaded, setMediaLoaded] = useState(false);
  // V43 — explicit retry state. Previously onError silently advanced to the
  // next story which was confusing. Now: if load fails OR stalls for 8s,
  // show a centred "Tap to retry" pill so users have agency.
  const [loadError, setLoadError] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const stallTimerRef = useRef(null);
  const spinnerTimerRef = useRef(null);
  const imgRef = useRef(null);
  const rafRef = useRef();
  // initial timestamp is set inside an effect to keep render pure for the
  // react-hooks/purity lint rule.
  const startRef = useRef(0);
  const videoRef = useRef(null);
  const durationRef = useRef(STORY_DURATION_MS);

  const current = stories[idx];

  // Reset the media-loaded gate every time the active story switches so the
  // user never sees the previous frame's overlays floating over a freshly
  // loading image. Also resets the retry/spinner state machine.
  useEffect(() => {
    setMediaLoaded(false);
    setLoadError(false);
    setShowSpinner(false);

    // Defer spinner — if the media is already cached (warm via prefetch),
    // onLoad fires within ~50ms and the spinner never flashes.
    if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
    spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);

    // Stall guard — if nothing loaded after STALL_TIMEOUT_MS, surface the
    // retry pill so the user isn't stuck staring at a spinner.
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    stallTimerRef.current = setTimeout(() => {
      // Only fire if we're still waiting
      setLoadError(prev => prev || true);
    }, STALL_TIMEOUT_MS);

    return () => {
      if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    };
  }, [idx, current?.id, retryToken]);

  // Cache-bust check: if the <img> was already cached by the prefetcher, the
  // browser may NOT fire onLoad (Chrome/Safari quirk on cached resources).
  // After mount, check img.complete and synthesise the load event so the
  // mediaLoaded gate flips correctly.
  useEffect(() => {
    if (!current || current.media_type === 'video') return;
    const t = setTimeout(() => {
      const img = imgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setMediaLoaded(true);
        setLoadError(false);
      }
    }, 30);
    return () => clearTimeout(t);
  }, [idx, current, retryToken]);

  const handleMediaLoaded = useCallback(() => {
    setMediaLoaded(true);
    setLoadError(false);
    if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    setShowSpinner(false);
  }, []);

  const handleMediaError = useCallback(() => {
    // Don't auto-advance — show a retry pill. This is the explicit fix the
    // user asked for: "show a proper retry state instead of hanging".
    if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    setLoadError(true);
    setShowSpinner(false);
  }, []);

  const handleRetry = () => {
    setRetryToken(t => t + 1);  // forces effect re-run + re-mounts img
  };

  // Preload the next + previous story media in the background (Instagram-style).
  // Tap-to-next then feels instant because the image is already in HTTP cache.
  // Wrap in requestIdleCallback so the prefetch never competes with the
  // CURRENT story's load — competing requests are the #1 cause of "spinner
  // for 2s" on mid-range mobile networks.
  useEffect(() => {
    if (!stories.length) return;
    const neighbours = [stories[idx + 1], stories[idx - 1]].filter(Boolean);
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = ric(() => {
      neighbours.forEach((s) => {
        const url = optimizeMediaUrl(s.media_url, s.media_type);
        if (!url) return;
        if (s.media_type === 'video') {
          try {
            const v = document.createElement('video');
            v.preload = 'auto';
            v.src = url;
            v.muted = true;
          } catch { /* no-op */ }
        } else {
          const img = new Image();
          img.decoding = 'async';
          img.src = url;
        }
      });
    });
    return () => cancel(id);
  }, [idx, stories]);

  // Lock body scroll
  useEffect(() => {
    startRef.current = Date.now();
    document.body.classList.add('chat-locked');
    return () => document.body.classList.remove('chat-locked');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/stories/user/${userId}`);
        setUser(data.user);
        setStories(data.stories || []);
      } catch { navigate(-1); }
      setLoading(false);
    };
    load();
  }, [userId, navigate]);

  const goNext = useCallback(() => {
    setIdx(i => {
      if (i + 1 >= stories.length) {
        navigate(-1);
        return i;
      }
      setStoryAnimKey(k => k + 1);
      return i + 1;
    });
    setProgress(0);
    startRef.current = Date.now();
  }, [stories.length, navigate]);

  const goPrev = () => {
    setIdx(i => {
      const next = Math.max(0, i - 1);
      if (next !== i) setStoryAnimKey(k => k + 1);
      return next;
    });
    setProgress(0);
    startRef.current = Date.now();
  };

  // Mark viewed when current changes (guard against StrictMode double-invoke)
  const viewedRef = useRef(new Set());
  useEffect(() => {
    if (!current) return;
    if (viewedRef.current.has(current.id)) return;
    viewedRef.current.add(current.id);
    api.post(`/stories/${current.id}/view`).catch(() => {});
  }, [current]);

  // Progress animation — gated on mediaLoaded so the 5s countdown doesn't
  // start ticking while the image is still being downloaded. Otherwise a
  // slow-network user would see the bar reach 50% before the photo even
  // appears.
  useEffect(() => {
    if (!current || paused || !mediaLoaded) return;
    const isVideo = current.media_type === 'video';
    durationRef.current = isVideo ? (videoRef.current?.duration * 1000 || 10000) : STORY_DURATION_MS;
    startRef.current = Date.now() - progress * durationRef.current;

    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const p = Math.min(1, elapsed / durationRef.current);
      setProgress(p);
      if (p >= 1) {
        goNext();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, paused, mediaLoaded]);

  // Handle video ended
  const handleVideoEnded = () => goNext();
  const handleVideoMeta = () => {
    if (videoRef.current && current?.media_type === 'video') {
      durationRef.current = videoRef.current.duration * 1000;
    }
  };

  // Tap handlers
  const handleTap = (e) => {
    const w = e.currentTarget.clientWidth;
    const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
    if (x < w * 0.35) goPrev();
    else if (x > w * 0.65) goNext();
  };

  // Long-press for pause
  const pressTimer = useRef(null);
  const handlePressStart = () => {
    pressTimer.current = setTimeout(() => {
      setPaused(true);
      if (videoRef.current) videoRef.current.pause();
    }, 200);
  };
  const handlePressEnd = () => {
    clearTimeout(pressTimer.current);
    if (paused) {
      setPaused(false);
      if (videoRef.current) videoRef.current.play().catch(() => {});
    }
  };

  // Viewers sheet state — extracted so swipe-up + button both share it
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersOffset, setViewersOffset] = useState(0);
  const [viewersHasMore, setViewersHasMore] = useState(false);
  const viewerSentinelRef = useRef(null);
  const VIEWERS_PAGE = 30;

  const fetchViewers = useCallback(async (offset = 0, replace = true) => {
    if (!current?.id || !current?.is_mine) return;
    setViewersLoading(true);
    try {
      const { data } = await api.get(
        `/stories/${current.id}/viewers?limit=${VIEWERS_PAGE}&offset=${offset}`
      );
      setViewersHasMore(!!data.has_more);
      setViewersOffset(offset + VIEWERS_PAGE);
      setViewers(prev => ({
        count: data.count ?? 0,
        viewers: replace ? (data.viewers || []) : [...prev.viewers, ...(data.viewers || [])],
      }));
    } catch { /* ignore */ }
    setViewersLoading(false);
  }, [current?.id, current?.is_mine]);

  const openViewers = useCallback(async () => {
    if (!current?.is_mine) return;
    setPaused(true);
    setShowViewers(true);
    setViewersOffset(0);
    setViewers({ count: 0, viewers: [] });
    await fetchViewers(0, true);
  }, [current?.is_mine, fetchViewers]);

  // Infinite scroll inside the viewers sheet
  useEffect(() => {
    if (!showViewers || !viewerSentinelRef.current || !viewersHasMore || viewersLoading) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && viewersHasMore && !viewersLoading) {
        fetchViewers(viewersOffset, false);
      }
    }, { rootMargin: '120px' });
    obs.observe(viewerSentinelRef.current);
    return () => obs.disconnect();
  }, [showViewers, viewersHasMore, viewersLoading, viewersOffset, fetchViewers]);

  const handleFollowViewer = async (v, e) => {
    e.stopPropagation();
    if (v._busy) return;  // race-guard against rapid double-tap
    const willFollow = !v.is_following;
    setViewers(prev => ({
      ...prev,
      viewers: prev.viewers.map(x => x.id === v.id ? { ...x, is_following: willFollow, _busy: true } : x),
    }));
    try {
      if (willFollow) await api.post(`/users/${v.id}/follow`);
      else await api.delete(`/users/${v.id}/follow`);
      setViewers(prev => ({
        ...prev,
        viewers: prev.viewers.map(x => x.id === v.id ? { ...x, _busy: false } : x),
      }));
    } catch {
      setViewers(prev => ({
        ...prev,
        viewers: prev.viewers.map(x => x.id === v.id ? { ...x, is_following: !willFollow, _busy: false } : x),
      }));
    }
  };

  const handleMessageViewer = async (v, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post('/dm/threads', { other_user_id: v.id });
      setShowViewers(false);
      navigate(`/dm/${data.id || data.thread_id}`);
    } catch { /* ignore */ }
  };

  // Swipe-down to close — also handles SWIPE-UP on own stories to open viewers.
  // Swipe handlers — supports both swipe-down (close) AND swipe-up (open
  // viewers sheet on own stories, Instagram-style).
  const handleTouchStart = (e) => { setTouchStartY(e.touches[0].clientY); setDragY(0); };
  const handleTouchMove = (e) => {
    if (touchStartY === null) return;
    const dy = e.touches[0].clientY - touchStartY;
    // Only translate the viewer for downward drags. Upward drags are
    // gestures, not card drags — don't move the canvas.
    if (dy > 0) setDragY(dy);
  };
  const handleTouchEnd = (e) => {
    if (touchStartY === null) return;
    const endY = (e.changedTouches && e.changedTouches[0]?.clientY) ?? touchStartY;
    const dy = endY - touchStartY;
    if (dy > 100) {
      navigate(-1);
    } else if (dy < -60 && current?.is_mine && !showViewers) {
      // Swipe-up on own story → open viewers (matches Instagram)
      openViewers();
    }
    setDragY(0);
    setTouchStartY(null);
  };

  const handleDelete = async () => {
    if (!current) return;
    try { await api.delete(`/stories/${current.id}`); } catch {/* ignore */}
    setShowConfirmDelete(false);
    // remove from local, advance or close
    const next = stories.filter(s => s.id !== current.id);
    if (next.length === 0) {
      navigate(-1);
    } else {
      setStories(next);
      setIdx(i => Math.min(i, next.length - 1));
      setProgress(0);
    }
  };

  if (loading || !current) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-[10050]">
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black z-[10050] overflow-hidden"
      data-testid="story-viewer"
      style={{ transform: `translateY(${dragY}px)`, opacity: 1 - Math.min(0.6, dragY / 400), transition: touchStartY === null ? 'transform 0.25s, opacity 0.25s' : 'none' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 flex gap-1 px-2 pt-2 z-10" data-testid="story-progress">
        {stories.map((s, i) => (
          <div key={s.id} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white"
              style={{
                width: `${i < idx ? 100 : i === idx ? progress * 100 : 0}%`,
                transition: i === idx && !paused ? 'none' : 'width 0.1s linear',
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-3 left-0 right-0 flex items-center gap-2.5 px-3 z-10 pointer-events-none">
        <button
          onClick={() => {
            if (!user?.id) return;
            // Pause and exit story viewer before navigating away
            setPaused(true);
            navigate(`/user/${user.id}`);
          }}
          data-testid="story-author-btn"
          aria-label={`Open ${user?.display_name || 'author'}'s profile`}
          className="flex items-center gap-2.5 flex-1 min-w-0 pointer-events-auto active:scale-95 transition-transform"
        >
          {user?.profile_photo ? (
            <img loading="lazy" decoding="async" src={user.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold text-white">{initials(user?.display_name)}</div>
          )}
          <div className="min-w-0 flex-1 text-left">
            <p className="text-sm font-bold text-white truncate" data-testid="story-author-name">{user?.display_name}</p>
            <p className="text-[11px] text-white/70">{timeAgo(current.created_at)}</p>
          </div>
        </button>
        {current.is_mine && (
          <button
            data-testid="story-delete-btn"
            onClick={() => { setPaused(true); setShowConfirmDelete(true); }}
            className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center pointer-events-auto"
            aria-label="Delete story"
          >
            <Trash2 size={16} className="text-white" />
          </button>
        )}
        <button
          data-testid="story-close-btn"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center pointer-events-auto"
          aria-label="Close"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* Media + overlays — 9:16 canvas matching editor geometry */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          key={storyAnimKey}
          className="relative overflow-hidden story-cinema-frame"
          style={{
            height: '100dvh',
            width: 'min(100vw, calc(100dvh * 9 / 16))',
            background: '#000',
          }}
        >
          {/* Blurred background fill for non-9:16 sources */}
          {current.media_type === 'video' ? (
            <video
              src={current.media_url}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'blur(38px) brightness(0.55) saturate(1.2)', transform: 'scale(1.2)' }}
              aria-hidden="true"
            />
          ) : (
            <img loading="eager" decoding="async" fetchPriority="high"
              src={optimizeMediaUrl(current.media_url, 'image')}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'blur(38px) brightness(0.55) saturate(1.2)', transform: 'scale(1.2)' }}
              aria-hidden="true"
            />
          )}

          {/* Foreground — object-contain so original framing is preserved */}
          {current.media_type === 'video' ? (
            <video
              ref={videoRef}
              src={optimizeMediaUrl(current.media_url, 'video')}
              autoPlay
              playsInline
              preload="auto"
              onLoadedMetadata={handleVideoMeta}
              onLoadedData={handleMediaLoaded}
              onCanPlay={handleMediaLoaded}
              onEnded={handleVideoEnded}
              onError={handleMediaError}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ filter: viewerFilterCss(current.filter), opacity: mediaLoaded ? 1 : 0, transition: 'opacity 180ms ease-out' }}
              data-testid="story-media-video"
            />
          ) : (
            <img ref={imgRef} loading="eager" decoding="async" fetchPriority="high"
              key={retryToken}
              src={optimizeMediaUrl(current.media_url, 'image')}
              alt=""
              onLoad={handleMediaLoaded}
              onError={handleMediaError}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ filter: viewerFilterCss(current.filter), opacity: mediaLoaded ? 1 : 0, transition: 'opacity 180ms ease-out' }}
              data-testid="story-media-image"
              draggable={false}
            />
          )}

          {/* Spinner — deferred 250ms so cached images never flash it. Hides
              immediately once media is loaded OR an error/stall is detected. */}
          {!mediaLoaded && !loadError && showSpinner && (
            <div
              data-testid="story-media-skeleton"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              aria-hidden="true"
            >
              <div className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white/80 animate-spin" />
            </div>
          )}

          {/* Retry state — explicit user agency. Replaces the previous silent
              auto-advance on error AND the infinite spinner on stall. */}
          {!mediaLoaded && loadError && (
            <div
              data-testid="story-media-retry"
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <RotateCcw size={22} className="text-white" />
              </div>
              <p className="text-white text-sm font-bold">Couldn&apos;t load this story</p>
              <p className="text-white/70 text-[12px] -mt-1">Check your connection</p>
              <button
                data-testid="story-media-retry-btn"
                onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                className="mt-1 bg-[#B6E600] text-[#111] font-extrabold text-sm px-5 py-2 rounded-full active:scale-95"
              >
                Tap to retry
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="text-white/60 text-[11px] font-bold underline mt-1"
                data-testid="story-media-skip"
              >
                Skip to next
              </button>
            </div>
          )}

          {/* Q16 — Overlays gate: text/stickers/mentions/location only render
              AFTER the underlying media is loaded. Beta users reported seeing
              floating overlays on a black background while images downloaded. */}
          {mediaLoaded && (
            <>
              {/* Overlays — preserved text and stickers from editor (exact position, scale, rotation) */}
              {(current.overlays || []).map((ov, idx) => (
                <ViewerOverlay key={ov.id || idx} ov={ov} />
              ))}

              {/* Location chip — ONLY when location came from the legacy text field
                 (NOT when a location sticker is in overlays, which renders itself
                 at the user's placed position/scale/rotation). */}
              {current.location && !(current.overlays || []).some(o => o.type === 'sticker' && o.sticker?.type === 'location') && (
                <div
                  className="absolute left-1/2 bottom-[18%] -translate-x-1/2 px-3 py-1.5 rounded-full bg-white/90 text-[#111] text-xs font-bold flex items-center gap-1 shadow-lg"
                  data-testid="story-location-chip"
                >
                  <span>📍</span>
                  {current.location}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Explicit tap zones — left=back, center=hold-to-pause, right=next.
          Below header (top-12) and above caption (bottom-20). */}
      <div className="absolute inset-x-0 z-10" style={{ top: 56, bottom: 96 }} data-testid="story-tap-zones">
        <div className="flex h-full w-full">
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            onPointerDown={handlePressStart}
            onPointerUp={handlePressEnd}
            onPointerCancel={handlePressEnd}
            onPointerLeave={handlePressEnd}
            className="flex-[3] focus:outline-none"
            data-testid="story-tap-left"
            aria-label="Previous story"
          />
          <button
            onPointerDown={handlePressStart}
            onPointerUp={handlePressEnd}
            onPointerCancel={handlePressEnd}
            onPointerLeave={handlePressEnd}
            className="flex-[2] focus:outline-none"
            data-testid="story-tap-center"
            aria-label="Hold to pause"
          />
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            onPointerDown={handlePressStart}
            onPointerUp={handlePressEnd}
            onPointerCancel={handlePressEnd}
            onPointerLeave={handlePressEnd}
            className="flex-[3] focus:outline-none"
            data-testid="story-tap-right"
            aria-label="Next story"
          />
        </div>
      </div>

      {/* Caption — only shown if author typed one (separate from overlays) */}
      {current.caption && (
        <div className="absolute bottom-[max(env(safe-area-inset-bottom),16px)] left-0 right-0 px-6 text-center pointer-events-none">
          <p className="text-white text-sm font-medium drop-shadow-lg whitespace-pre-wrap" data-testid="story-caption">
            {renderMentionText(current.caption)}
          </p>
        </div>
      )}

      {/* Bottom bar — Viewers (own) or Reply input (others') */}
      {current.is_mine ? (
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 z-30 pointer-events-none">
          {/* V43 — Full-width tappable pill with chevron-up affordance.
              Previously a tiny "1 view" chip in the corner; users missed it
              entirely. Now it's a generous bottom strip AND swipe-up on the
              media also opens the sheet. */}
          <button
            onClick={openViewers}
            data-testid="story-viewers-btn"
            aria-label={`See who viewed this story (${current.view_count || 0})`}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-black/55 backdrop-blur text-white font-bold pointer-events-auto active:scale-[0.97] transition-transform"
          >
            <ChevronUp size={16} className="opacity-80" strokeWidth={2.5} />
            <Eye size={15} strokeWidth={2.4} />
            <span className="text-[13px]">
              {current.view_count || 0} {current.view_count === 1 ? 'view' : 'views'}
            </span>
            <span className="text-[11px] font-medium text-white/65 ml-1">· Swipe up</span>
          </button>
        </div>
      ) : (
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-[max(env(safe-area-inset-bottom),10px)] pt-2 z-30 flex items-center gap-2" onPointerDown={e => e.stopPropagation()}>
          <input
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onFocus={() => setPaused(true)}
            onBlur={() => { if (!replyText) setPaused(false); }}
            onKeyDown={async e => {
              if (e.key === 'Enter' && replyText.trim() && !replySending) {
                setReplySending(true);
                try {
                  await api.post(`/stories/${current.id}/reply`, { text: replyText.trim() });
                  setReplyText('');
                  setReplySent(true);
                  setTimeout(() => setReplySent(false), 1800);
                } catch {/* ignore */}
                setReplySending(false);
              }
            }}
            placeholder={`Reply to ${user?.display_name?.split(' ')[0] || user?.username || 'story'}…`}
            data-testid="story-reply-input"
            className="flex-1 bg-white/15 backdrop-blur text-white text-sm rounded-full px-4 py-2.5 focus:outline-none border border-white/25 placeholder-white/50"
          />
          <button
            disabled={!replyText.trim() || replySending}
            onClick={async () => {
              setReplySending(true);
              try {
                await api.post(`/stories/${current.id}/reply`, { text: replyText.trim() });
                setReplyText('');
                setReplySent(true);
                setTimeout(() => setReplySent(false), 1800);
              } catch {/* ignore */}
              setReplySending(false);
            }}
            data-testid="story-reply-send"
            className="w-10 h-10 rounded-full bg-[#B6E600] text-[#111111] flex items-center justify-center disabled:opacity-40 active:scale-90"
          >
            <Send size={16} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {replySent && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-[#B6E600] text-[#111111] text-xs font-bold px-4 py-2.5 rounded-full shadow-lg z-[10020]" data-testid="story-reply-sent">
          Reply sent ✓
        </div>
      )}

      {/* Viewers sheet — paginated, no timestamps, inline Follow + Message */}
      {showViewers && (
        <div className="fixed inset-0 z-[10070] flex items-end bg-black/60 backdrop-blur-sm" onClick={() => { setShowViewers(false); setPaused(false); }} data-testid="story-viewers-overlay">
          <div className="w-full max-w-[480px] mx-auto max-h-[78vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()} data-testid="story-viewers-sheet">
            <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-[#E5E4DE] rounded-full" /></div>
            <div className="px-5 pt-2 pb-3 flex items-center justify-between border-b border-black/[0.04]">
              <p className="text-base font-extrabold text-[#111111] flex items-center gap-2" data-testid="story-viewers-title">
                <Eye size={16} className="text-[#3F5200]" />
                Seen by <span className="tabular-nums">{viewers.count}</span>
              </p>
              <button onClick={() => { setShowViewers(false); setPaused(false); }} data-testid="story-viewers-close" className="text-[#9B9EA3] active:scale-90"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 pb-[max(env(safe-area-inset-bottom),16px)]" data-testid="story-viewers-scroll">
              {viewersLoading && viewers.viewers.length === 0 ? (
                <div className="space-y-2.5 px-1">
                  {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-14 rounded-2xl" />)}
                </div>
              ) : viewers.viewers.length === 0 ? (
                <p className="text-center text-xs text-[#9B9EA3] py-8" data-testid="story-viewers-empty">No one has seen this story yet.</p>
              ) : (
                <>
                  {viewers.viewers.map(v => (
                    <div key={v.id} data-testid={`viewer-row-${v.id}`} className="w-full flex items-center gap-3 p-2.5 rounded-2xl transition-colors">
                      <button
                        onClick={() => { setShowViewers(false); navigate(`/user/${v.id}`); }}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left active:opacity-70"
                        data-testid={`viewer-open-profile-${v.id}`}
                      >
                        {v.profile_photo
                          ? <img loading="lazy" decoding="async" src={v.profile_photo} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                          : <div className="w-11 h-11 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3] flex-shrink-0">{initials(v.display_name)}</div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-[#111111] truncate">{v.display_name}</p>
                          <p className="text-[12px] text-[#9B9EA3] truncate">@{v.username}</p>
                        </div>
                      </button>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button
                          data-testid={`viewer-follow-${v.id}`}
                          onClick={(e) => handleFollowViewer(v, e)}
                          className={`text-[11px] font-extrabold px-3 py-1.5 rounded-full active:scale-95 transition-colors ${
                            v.is_following
                              ? 'bg-[#F1F0EA] text-[#6B6E73]'
                              : 'bg-[#B6E600] text-[#111111]'
                          }`}
                        >
                          {v.is_following ? 'Following' : 'Follow'}
                        </button>
                        <button
                          data-testid={`viewer-message-${v.id}`}
                          onClick={(e) => handleMessageViewer(v, e)}
                          aria-label="Message"
                          className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center active:scale-90"
                        >
                          <MessageCircle size={15} className="text-[#111]" strokeWidth={2.4} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {/* Infinite-scroll sentinel */}
                  <div ref={viewerSentinelRef} className="py-3 text-center">
                    {viewersLoading && viewers.viewers.length > 0 && (
                      <p className="text-[11px] text-[#9B9EA3] font-bold">Loading more…</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {showConfirmDelete && (
        <div
          className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/60 px-6"
          onClick={() => { setShowConfirmDelete(false); setPaused(false); }}
          data-testid="story-delete-confirm-overlay"
        >
          <div
            className="w-full max-w-[320px] bg-white rounded-3xl p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-[#111111]">Delete story?</h3>
            <p className="text-sm text-[#6B6E73] mt-1">This story will be removed from everyone.</p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                onClick={() => { setShowConfirmDelete(false); setPaused(false); }}
                className="py-3 rounded-full bg-[#F1F0EA] text-sm font-semibold text-[#111111]"
              >
                Cancel
              </button>
              <button
                data-testid="story-delete-confirm-btn"
                onClick={handleDelete}
                className="py-3 rounded-full bg-[#FF3B30] text-sm font-bold text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Helpers: render overlays and mention-highlighted text
// ---------------------------------------------------------------

// CSS filter presets — must match StoryEditor FILTERS
const VIEWER_FILTERS = {
  '': 'none',
  bw:    'grayscale(1) contrast(1.05)',
  vivid: 'saturate(1.4) contrast(1.1)',
  warm:  'saturate(1.15) contrast(1.05) brightness(1.02) sepia(0.08)',
  cool:  'saturate(1.05) contrast(1.08) brightness(1) hue-rotate(-8deg)',
  fade:  'saturate(0.7) contrast(0.92) brightness(1.05)',
  mono:  'grayscale(1) brightness(1.05) contrast(0.95)',
  glow:  'saturate(1.1) brightness(1.08) blur(0.4px)',
};
function viewerFilterCss(key) { return VIEWER_FILTERS[key || ''] || 'none'; }

const VIEWER_FONTS = {
  classic:    'font-bold tracking-tight',
  modern:     'font-black tracking-tighter',
  neon:       'font-extrabold italic tracking-wide',
  typewriter: 'font-mono font-bold',
  strong:     'font-black uppercase tracking-widest',
  // Legacy keys still supported
  display:    'font-black tracking-tight',
  mono:       'font-mono font-bold',
  serif:      'font-serif font-bold italic',
};

const PILL_KINDS = ['none', 'solid', 'translucent', 'outline'];

function withAlpha(hex, a) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
function pickInk(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return '#111111';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? '#111111' : '#FFFFFF';
}

function ViewerOverlay({ ov }) {
  const transform = `translate(-50%, -50%) scale(${ov.scale || 1}) rotate(${ov.rotation || 0}deg)`;
  const commonStyle = {
    position: 'absolute',
    top: `${ov.y ?? 50}%`,
    left: `${ov.x ?? 50}%`,
    transform,
    userSelect: 'none',
  };

  if (ov.type === 'sticker') {
    const s = ov.sticker || {};
    // Link sticker is the only tappable surface for viewers
    if (s.type === 'link' && (s.url || s.label)) {
      const href = s.url || (s.label.startsWith('http') ? s.label : `https://${s.label}`);
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...commonStyle, pointerEvents: 'auto' }}
          data-testid="viewer-link-sticker"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/75 text-white text-xs font-extrabold shadow-md no-underline"
        >
          🔗 {s.label || s.url}
        </a>
      );
    }
    if (s.type === 'location') {
      return (
        <span style={{ ...commonStyle, pointerEvents: 'none' }} data-testid="viewer-location-sticker"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B6E600] text-[#111111] text-xs font-extrabold shadow-md">
          📍 {s.name || 'Location'}
        </span>
      );
    }
    if (s.type === 'mention') {
      return (
        <span style={{ ...commonStyle, pointerEvents: 'none' }} data-testid="viewer-mention-sticker"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-[#111111] text-xs font-extrabold shadow-md">
          @{s.username || 'user'}
        </span>
      );
    }
    if (s.type === 'poll') {
      return (
        <div style={{ ...commonStyle, pointerEvents: 'none' }} data-testid="viewer-poll-sticker"
             className="px-3 py-2 rounded-2xl bg-white/90 text-[#111111] shadow-md min-w-[180px]">
          <p className="text-[11px] font-bold text-center mb-1.5">{s.question || 'Yes or no?'}</p>
          <div className="flex gap-1.5">
            <span className="flex-1 text-center py-1 rounded-full bg-[#B6E600] text-[11px] font-bold">{s.optionA || 'Yes'}</span>
            <span className="flex-1 text-center py-1 rounded-full bg-[#FF6B9D] text-white text-[11px] font-bold">{s.optionB || 'No'}</span>
          </div>
        </div>
      );
    }
    // emoji + legacy `ov.emoji` fallback
    const emoji = s.value || ov.emoji || '✨';
    return (
      <div style={{ ...commonStyle, fontSize: 64, lineHeight: 1, pointerEvents: 'none' }} data-testid="viewer-sticker">
        {emoji}
      </div>
    );
  }

  // text overlay
  const cls = VIEWER_FONTS[ov.fontKey] || VIEWER_FONTS.classic;
  const align = ov.align || 'center';
  const pillKind = PILL_KINDS[ov.pill || 0];
  let bg = 'transparent';
  let textStyle = {};
  if (pillKind === 'solid') {
    bg = ov.color || '#FFFFFF';
    textStyle.color = pickInk(bg);
  } else if (pillKind === 'translucent') {
    bg = withAlpha(ov.color || '#FFFFFF', 0.35);
    textStyle.color = '#FFFFFF';
  } else if (pillKind === 'outline') {
    textStyle.color = ov.color || '#FFFFFF';
    textStyle.WebkitTextStroke = `1.5px ${pickInk(ov.color || '#FFFFFF')}`;
    textStyle.textShadow = '0 1px 2px rgba(0,0,0,0.55)';
  } else {
    textStyle.color = ov.color || '#FFFFFF';
    textStyle.textShadow = '0 1px 3px rgba(0,0,0,0.45)';
  }
  if (ov.underline) textStyle.textDecoration = 'underline';
  textStyle.opacity = ov.opacity ?? 1;
  // CRITICAL: use display:block + width:max-content so text-align actually
  // affects multi-line wrapping (the legacy renderer used inline-block which
  // collapsed to text width and made align look broken).
  const padded = bg !== 'transparent' ? 'px-3 py-1.5 rounded-lg' : '';
  return (
    <div
      className={cls}
      data-testid="viewer-text"
      style={{
        ...commonStyle,
        pointerEvents: 'none',
        fontSize: 28,
        lineHeight: 1.18,
        padding: '6px 12px',
        maxWidth: '82%',
        width: 'max-content',
        whiteSpace: 'pre-wrap',
        textAlign: align,
        wordBreak: 'break-word',
        background: bg,
        borderRadius: bg !== 'transparent' ? 8 : 0,
        ...textStyle,
      }}
    >
      {renderMentionText(ov.text || '')}
    </div>
  );
}

function renderMentionText(text) {
  const re = /@[a-zA-Z0-9_]{1,30}/g;
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={`t-${i++}`}>{text.slice(last, m.index)}</span>);
    out.push(<span key={`m-${i++}`} style={{ color: '#B6E600', fontWeight: 900 }}>{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={`t-${i++}`}>{text.slice(last)}</span>);
  return out;
}

